<template>
  <div class="column full-height">
    <header
      class="row items-center justify-between q-px-2x-md full-width no-wrap"
      :style="{ height: '86px' }"
    >
      <div class="column">
        <span class="text-lg text-neutral text-weight-medium">
          {{ debitNote?.number || "-" }}
        </span>
      </div>
      <div>
        <PBtn dense flat @click="openFilesDrawer">
          <DocumentIcon color="primary" class="icon--xs q-mr-xxs" />
          <span class="text-xs">
            Files ({{ debitNote?.dn_file?.length || 0 }})
          </span>
        </PBtn>
        <span>
          <PBadge
            :label="debitNote.status"
            :type="debitNote.status == 'Active' ? 'success' : 'Cancelled'"
            class="q-ml-sm q-mr-md text-xs"
          />
          <PTooltip
            v-if="debitNote.status === 'Cancelled' && debitNote?.reason?.length"
          >
            {{ debitNote.reason }}
          </PTooltip>
        </span>
        <PBtn color="primary" flat round @click="onClose">
          <CloseIcon class="icon" />
        </PBtn>
      </div>
    </header>
    <q-separator color="neutral-40" />
    <div class="col full-width overflow-y-scroll">
      <UserDetails :user-details="debitNote" :source="source" :module="'dn'" />
      <q-separator class="q-ma-xxl" />
      <div class="overflow-hidden q-mx-2x-md q-mt-xl">
        <legend class="q-mb-sm text-neutral text-weight-medium text-md">
          Items
        </legend>
        <PTable
          v-if="debitNote?.lineitems?.length"
          :pagination="pagination"
          rows-per-page-label=" "
          :rows-per-page-options="[]"
          :columns="productListColumns"
          :rows="debitNote.lineitems"
          :style="{ margin: '0 -15px', padding: 0 }"
          table-header-class="text-neutral-70"
          dense
          flat
          wrap-cells
        >
          <template #body-cell="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <PEditable v-model="cellProps.row[props.col.name]" disable />
            </q-td>
          </template>

          <template #body-cell-name="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <ItemNameCell :col="cellProps.col" :table-props="cellProps" />
            </q-td>
          </template>

          <template #body-cell-qty="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <ItemQuantityCell :col="cellProps.col" :table-props="cellProps" />
            </q-td>
          </template>

          <template #body-cell-rate="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <ItemRateCell :col="cellProps.col" :table-props="cellProps" />
            </q-td>
          </template>

          <template #body-cell-amount="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <ItemAmountCell :col="cellProps.col" :table-props="cellProps" />
            </q-td>
          </template>

          <template #body-cell-po_qty="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <div class="align-middle">
                <p class="q-mb-none">{{ roundOff(props.value) }}</p>
                <p class="q-mb-none">{{ props.row.po_unit }}</p>
              </div>
            </q-td>
          </template>

          <template #body-cell-conversion_factor="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <span
                v-if="cellProps.row.unit === cellProps.row.po_unit"
                class="ellipsis text-neutral-90 text-xs"
              >
                Same Units
              </span>
              <span v-else class="ellipsis text-neutral-90 text-xs">
                {{ props.row.is_po_unit_bigger ? props.value : 1 }}
                {{ props.row.unit || "Unit" }}
                =
                {{ props.row.is_po_unit_bigger ? 1 : props.value }}
                {{ props.row.po_unit || "Unit" }}
              </span>
            </q-td>
          </template>
          <template #body-cell-po_rate="cellProps">
            <q-td :key="cellProps.col.name" :props="cellProps">
              <span class="ellipsis text-neutral text-xs">
                {{ props.value }}
              </span>
            </q-td>
          </template>
        </PTable>

        <span
          v-if="debitNote?.is_freight_included"
          class="text-xxs text-neutral text-italic"
        >
          <sup>*</sup>Freight is included in the rate
        </span>
      </div>

      <div class="row q-px-2x-md q-mt-xl text-sm text-neutral">
        <div class="col-6">
          <div>
            <span class="text-neutral-80">Remarks </span>
          </div>
          <div>
            <span> {{ debitNote?.remarks || "" }} </span>
          </div>
        </div>
        <div class="col-6">
          <div class="row q-col-gutter-xs">
            <div class="col-6">
              <p class="q-mb-none">Total Amount</p>
              <span class="text-xs text-neutral-70"> Excludes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: getTotalAmountWithoutTax(),
                    style: "currency",
                  })
                }}
              </div>
            </div>

            <div class="col-6">
              <p class="q-mb-none">Tax Total</p>
              <span class="text-xs text-neutral-70"> GST + CESS </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: debitNote.total_tax,
                    style: "currency",
                  })
                }}
              </div>
            </div>

            <div class="col-6">
              <p class="q-mb-none">Grand Total</p>
              <span class="text-xs text-neutral-70"> Includes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: debitNote.total_amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div v-if="source == 'sales'" class="row q-mt-xs">
            <div
              v-if="debitNote?.buyer?.tax_settings?.percentage"
              class="col-6 q-mb-md"
            >
              <span
                >TCS ({{ debitNote.buyer?.tax_settings?.percentage }}%)</span
              >
              <PTooltip
                anchor="bottom left"
                self="top left"
                :offset="[0, 4]"
                class="text-sm"
                max-width="500px"
              >
                {{ debitNote.buyer?.tax_settings?.name }}
              </PTooltip>
            </div>
            <div v-if="debitNote.buyer?.tax_settings?.percentage" class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number:
                      (debitNote?.grand_total *
                        debitNote?.buyer?.tax_settings?.percentage) /
                        100 || 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
            <div class="col-6">
              <p>Payments Received</p>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{ debitNote?.payment_summary?.total_paid_amount ? "-" : "" }}
                {{
                  numberFormat({
                    number: debitNote?.payment_summary?.total_paid_amount ?? 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
            <div class="col-6">
              <p>Balance Due</p>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: debitNote?.payment_summary?.balance_due ?? 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div v-if="source == 'purchases'" class="row q-mt-xs">
            <div v-if="debitNote?.tds" class="col-6 q-mb-md">
              <TdsInfoTooltip :percentage="debitNote?.tds?.percentage" />
            </div>
            <div v-if="debitNote?.tds" class="col-6">
              <div class="text-right">
                {{ debitNote?.tds?.amount ? "-" : "" }}
                {{
                  numberFormat({
                    number: debitNote?.tds?.amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
            <div class="col-6">
              <p>Remaining Credits</p>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: debitNote?.remaining_credits ?? 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <q-separator class="q-ma-xxl" />

      <div class="q-px-2x-md q-mt-xl q-pb-2x-xl">
        <AssociatedVouchers
          :id="debitNote?._id"
          :type="
            source === 'purchases' ? 'pb_dn' : source === 'sales' ? 'sl_dn' : ''
          "
        />
      </div>
    </div>
  </div>
</template>
<script setup>
import CloseIcon from "assets/icons/close.svg";
import DocumentIcon from "assets/icons/document.svg";
import { date } from "quasar";
import { numberFormat, roundOff } from "assets/scripts/utilities/helpers";
import PBadge from "src/components/PBadge.vue";
import UserDetails from "src/modules/credit-notes/components/UserDetails.vue";

const { formatDate } = date;
const { toggleDebitNoteDetailsDrawer } = useDebitNoteStore();
const { debitNote } = storeToRefs(useDebitNoteStore());
const { toggleAssociatedDataDrawer } = usePaymentRequestStore();
const { removeQueryParams } = useRouterUtilities();

const { viewFile } = useFile();

const props = defineProps({ source: { type: String, default: "" } });

const onClose = () => {
  removeQueryParams();

  toggleDebitNoteDetailsDrawer({ open: false });
};

const { pagination, getTotalAmountWithoutTax } = useProductList({
  formData: debitNote,
});

const productListColumns = computed(() => {
  let columns = [
    { align: "left", name: "name", label: "Name", field: "name" },
    { align: "left", name: "qty", label: "Qty", field: "qty" },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
      type: "currency",
    },
  ];

  return columns;
});

const status = useColor();

const filesColumns = computed(() => {
  return [
    {
      label: "Date",
      field: "created_date",
      format: (value) => {
        return formatDate(value, "DD MMM YYYY");
      },
    },
    {
      label: "Name",
      field: "file_name",
      cellClass: "text-primary text-weight-medium underline cursor-pointer",
      onClick: viewFile,
    },
    {
      align: "center",
      label: "Status",
      field: "status",
      format: (value) =>
        `<span class="bg-${status(value).color} text-${
          status(value).textColor
        } q-px-sm q-py-xxs rounded" title="Status">${value}</span>`,
      cellClass: "q-px-sm q-py-xxs rounded",
    },
  ];
});

const openFilesDrawer = () => {
  toggleAssociatedDataDrawer({
    edit: false,
    level: 0,
    open: true,
    view: true,
    props: {
      columns: unref(filesColumns),
      rows: debitNote.value?.dn_file,
      heading: "Associated Files",
      type: "file",
    },
  });
};
</script>
